import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Phone, Mail, MapPin, ArrowRight, Star, Shield, Clock, Home, 
  Building2, Landmark, Users, FileText, RefreshCw, ChevronRight,
  Linkedin, Instagram, Facebook, Menu, X
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Track analytics
const trackEvent = async (eventType, eventData = {}) => {
  try {
    await fetch(`${API_URL}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: eventType,
        event_data: eventData,
        timestamp: new Date().toISOString(),
        referrer: document.referrer || 'direct',
        user_agent: navigator.userAgent
      })
    });
  } catch (err) {
    console.log('Analytics tracking error:', err);
  }
};

// Navbar Component
const Navbar = ({ onApplyClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-slate-950/90 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img 
              src="https://mmtainc.com/wp-content/uploads/2025/06/moneyman_logo_white-150x150.png" 
              alt="MoneyMan Tee Ares" 
              className="w-12 h-12"
            />
            <span className="font-heading text-xl text-white font-semibold hidden sm:block">
              MoneyMan Tee
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('services')} className="text-slate-300 hover:text-gold transition-colors" data-testid="nav-services">
              Services
            </button>
            <button onClick={() => scrollToSection('about')} className="text-slate-300 hover:text-gold transition-colors" data-testid="nav-about">
              About
            </button>
            <button onClick={() => scrollToSection('testimonials')} className="text-slate-300 hover:text-gold transition-colors" data-testid="nav-testimonials">
              Reviews
            </button>
            <button onClick={() => scrollToSection('contact')} className="text-slate-300 hover:text-gold transition-colors" data-testid="nav-contact">
              Contact
            </button>
            <Button 
              onClick={onApplyClick}
              className="rounded-full bg-gold text-slate-950 font-bold px-6 py-2 hover:bg-gold-light hover:scale-105 transition-all shadow-[0_0_20px_rgba(212,175,55,0.3)]"
              data-testid="nav-apply-btn"
            >
              Apply Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-slate-950/95 backdrop-blur-xl border-t border-white/5 py-6">
            <div className="flex flex-col gap-4">
              <button onClick={() => scrollToSection('services')} className="text-slate-300 hover:text-gold py-2">Services</button>
              <button onClick={() => scrollToSection('about')} className="text-slate-300 hover:text-gold py-2">About</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-slate-300 hover:text-gold py-2">Reviews</button>
              <button onClick={() => scrollToSection('contact')} className="text-slate-300 hover:text-gold py-2">Contact</button>
              <Button onClick={onApplyClick} className="rounded-full bg-gold text-slate-950 font-bold mt-2">
                Apply Now
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Service Card Component
const ServiceCard = ({ icon: Icon, title, description, onClick }) => (
  <div 
    className="group relative overflow-hidden rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-md p-8 hover:border-gold/30 hover:-translate-y-2 hover:shadow-2xl hover:shadow-gold/10 transition-all duration-500 cursor-pointer"
    onClick={onClick}
    data-testid={`service-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="w-14 h-14 rounded-xl bg-gold/10 flex items-center justify-center mb-6 group-hover:bg-gold/20 transition-colors">
      <Icon className="w-7 h-7 text-gold" />
    </div>
    <h3 className="font-heading text-xl text-white mb-3">{title}</h3>
    <p className="text-slate-400 leading-relaxed">{description}</p>
    <div className="flex items-center gap-2 mt-4 text-gold opacity-0 group-hover:opacity-100 transition-opacity">
      <span className="text-sm font-medium">Learn More</span>
      <ChevronRight className="w-4 h-4" />
    </div>
  </div>
);

// Testimonial Card
const TestimonialCard = ({ name, review, rating }) => (
  <div className="min-w-[350px] md:min-w-[400px] bg-slate-900/60 backdrop-blur-md border border-white/5 rounded-2xl p-8 mx-4">
    <div className="flex gap-1 mb-4">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className={`w-5 h-5 ${i < rating ? 'text-gold fill-gold' : 'text-slate-600'}`} />
      ))}
    </div>
    <p className="text-slate-300 italic mb-6 leading-relaxed">"{review}"</p>
    <p className="text-white font-semibold">{name}</p>
  </div>
);

export default function HomePage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('');

  useEffect(() => {
    trackEvent('page_view', { page: 'homepage' });
  }, []);

  const handleApplyClick = () => {
    trackEvent('cta_click', { cta: 'apply_now', page: 'homepage' });
    navigate('/apply');
  };

  const handleCallClick = () => {
    trackEvent('cta_click', { cta: 'call', page: 'homepage' });
    window.location.href = 'tel:305-606-2423';
  };

  const handleTextClick = () => {
    trackEvent('cta_click', { cta: 'text', page: 'homepage' });
    window.location.href = 'sms:+13056062423';
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    trackEvent('form_submit', { form: 'newsletter', email });
    setSubscribeStatus('Thank you for subscribing!');
    setEmail('');
    setTimeout(() => setSubscribeStatus(''), 3000);
  };

  const services = [
    { icon: Home, title: 'First-Time Buyers', description: 'FHA, Conventional & 1% Down loans with step-by-step guidance to get you approved fast.' },
    { icon: Building2, title: 'Real Estate Investors', description: 'DSCR, Fix & Flip, and Rental Loans. Flexible options with no income docs needed.' },
    { icon: Users, title: 'Realtors & Agents', description: 'Win more offers. I\'ll pre-qualify buyers on-site and help promote your listings.' },
    { icon: FileText, title: 'Self-Employed', description: 'Bank Statement & 1099 Loans. No W2s required. Tailored solutions for business owners.' },
    { icon: RefreshCw, title: 'Refinance', description: 'Lower your rate, access cash, or shift into an investor-friendly loan strategy.' },
    { icon: Landmark, title: 'Hard Money', description: 'Fast, asset-based financing for investors needing flexible terms and quick closings.' },
  ];

  const testimonials = [
    { name: 'David & Yeny Parson', review: 'I been working with Tee for over 25 years in Real Estate. He always come through and helps us from start to finish.', rating: 5 },
    { name: 'Mark Kole', review: 'Jose has helped me in every turn of the way. Making it now over 13 years of working together. I can say he is the best at what he does. Highly recommend him.', rating: 5 },
    { name: 'Jovi Gonzalez', review: 'Been working with Tee for the past 15 years. No matter how hard the loan is to approve. He always has a solution. He has never let me down.', rating: 5 },
  ];

  return (
    <div className="min-h-screen bg-slate-950 font-body">
      <Navbar onApplyClick={handleApplyClick} />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1740482882563-bccef548b8dc?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div>
              <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-6">Your Partner in Smart Financing</p>
              <h1 className="font-heading text-5xl md:text-7xl text-white font-bold leading-tight mb-6">
                LENDING<br />
                <span className="text-gold">EXPERT</span>
              </h1>
              <p className="text-xl text-slate-300 mb-4">
                Hi, I'm <strong className="text-white">Jose Ares</strong>, aka <strong className="text-gold">MoneyMan Tee</strong>
              </p>
              <p className="text-lg text-slate-400 mb-8 max-w-lg">
                Helping first-time buyers, investors, and agents close faster with powerful lending solutions. I secure financing when no one else could.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  onClick={handleApplyClick}
                  size="lg"
                  className="rounded-full bg-gold text-slate-950 font-bold text-lg px-8 py-6 hover:bg-gold-light hover:scale-105 transition-all shadow-[0_0_30px_rgba(212,175,55,0.4)]"
                  data-testid="hero-apply-btn"
                >
                  Get Pre-Approved
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  onClick={handleCallClick}
                  size="lg"
                  variant="outline"
                  className="rounded-full border-2 border-slate-700 text-white hover:bg-slate-800 hover:border-white/30 font-bold text-lg px-8 py-6 transition-all"
                  data-testid="hero-call-btn"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  305-606-2423
                </Button>
              </div>

              <div className="flex items-center gap-6 text-slate-400">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-gold" />
                  <span className="text-sm">Licensed MLO</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-gold" />
                  <span className="text-sm">Same-Day Pre-Approval</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-gold" />
                  <span className="text-sm">5-Star Reviews</span>
                </div>
              </div>
            </div>

            {/* Image */}
            <div className="relative hidden lg:block">
              <div className="relative z-10">
                <img 
                  src="https://customer-assets.emergentagent.com/job_leadtrack-pro-2/artifacts/jvf8ujzx_Jose_Ares_moneyman.jpg"
                  alt="Jose Ares - MoneyMan Tee"
                  className="w-full max-w-lg mx-auto rounded-2xl shadow-2xl"
                />
              </div>
              <div className="absolute -top-8 -right-8 w-80 h-80 bg-gold/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-8 -left-8 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 md:py-32 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-4">What I Offer</p>
            <h2 className="font-heading text-4xl md:text-5xl text-white font-semibold mb-6">
              Mortgage & Lending Solutions
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg">
              Whether you're buying your first home, refinancing, or investing — I simplify the mortgage process and fight to get you the best deal.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <ServiceCard key={index} {...service} onClick={handleApplyClick} />
            ))}
          </div>
        </div>
      </section>

      {/* Loan Programs Banner */}
      <section className="py-16 bg-gradient-to-r from-gold/10 via-gold/5 to-gold/10 border-y border-gold/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="font-heading text-2xl md:text-3xl text-white font-medium mb-2">
                FHA Loans as Low as <span className="text-gold">580 Credit Score</span>
              </h3>
              <p className="text-slate-400">Tailored loan solutions for every situation</p>
            </div>
            <Button 
              onClick={handleApplyClick}
              className="rounded-full bg-gold text-slate-950 font-bold px-8 py-4 hover:bg-gold-light hover:scale-105 transition-all"
              data-testid="banner-apply-btn"
            >
              Check Your Eligibility
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 md:py-32 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_leadtrack-pro-2/artifacts/axpnuzvi_IMG_7954.PNG"
                alt="Jose Ares with Family"
                className="w-full max-w-md mx-auto rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-gold text-slate-950 rounded-2xl p-6 shadow-xl">
                <div className="text-4xl font-bold font-heading">25+</div>
                <div className="text-sm font-medium">Years Experience</div>
              </div>
            </div>

            {/* Content */}
            <div>
              <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-4">About Me</p>
              <h2 className="font-heading text-4xl md:text-5xl text-white font-semibold mb-6">
                Family-Focused.<br />Results-Driven.
              </h2>
              <p className="text-slate-300 text-lg mb-6 leading-relaxed">
                I'm Jose L. Ares — a former pro athlete turned Mortgage Loan Originator. I know how to hustle, and I bring that same energy to helping families make smarter real estate moves.
              </p>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Whether it's your first home or your forever one, I'm here to make it happen. With deep experience in investor-friendly, low-down-payment, and fast-close loans, I guide you step-by-step so you can focus on the big picture.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                    <Star className="w-5 h-5 text-gold" />
                  </div>
                  <span className="text-white">Tailored Loan Solutions — FHA, Conventional, DSCR & more</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-gold" />
                  </div>
                  <span className="text-white">Fast Pre-Approvals — Same day in competitive markets</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-gold" />
                  </div>
                  <span className="text-white">Investor-Friendly — DSCR, fix & flip, creative financing</span>
                </div>
              </div>

              <div className="flex gap-4">
                <Button 
                  onClick={handleApplyClick}
                  className="rounded-full bg-gold text-slate-950 font-bold px-8 py-4 hover:bg-gold-light transition-all"
                  data-testid="about-apply-btn"
                >
                  Book a Free Call
                </Button>
                <Button 
                  onClick={handleTextClick}
                  variant="outline"
                  className="rounded-full border-slate-700 text-white hover:bg-slate-800 px-8 py-4 transition-all"
                  data-testid="about-text-btn"
                >
                  Text Me Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 md:py-32 bg-slate-950 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-12">
          <div className="text-center">
            <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-4">Testimonials</p>
            <h2 className="font-heading text-4xl md:text-5xl text-white font-semibold mb-4">
              What My Clients Say
            </h2>
            <div className="flex items-center justify-center gap-2 mb-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 text-gold fill-gold" />
              ))}
            </div>
            <p className="text-slate-400">5.0 out of 5 — Yelp Reviews</p>
          </div>
        </div>

        {/* Marquee */}
        <div className="relative">
          <div className="flex animate-marquee">
            {[...testimonials, ...testimonials].map((testimonial, index) => (
              <TestimonialCard key={index} {...testimonial} />
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 md:py-32 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-4">Get in Touch</p>
              <h2 className="font-heading text-4xl md:text-5xl text-white font-semibold mb-6">
                Let's Talk About Your Goals
              </h2>
              <p className="text-slate-400 text-lg mb-8">
                Ready to take the next step? Reach out and let's discuss how I can help you achieve your real estate financing goals.
              </p>

              <div className="space-y-6 mb-8">
                <a href="tel:305-606-2423" className="flex items-center gap-4 text-white hover:text-gold transition-colors" data-testid="contact-phone">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Phone</p>
                    <p className="font-semibold">305-606-2423</p>
                  </div>
                </a>
                <a href="mailto:jares@axecapitallending.com" className="flex items-center gap-4 text-white hover:text-gold transition-colors" data-testid="contact-email">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Email</p>
                    <p className="font-semibold">jares@axecapitallending.com</p>
                  </div>
                </a>
                <div className="flex items-center gap-4 text-white">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Address</p>
                    <p className="font-semibold">8780 NW 18th Terr, Miami, FL 33172</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/jose-tee-ares-67428967/" target="_blank" rel="noopener noreferrer" 
                   className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-gold hover:text-slate-950 transition-all"
                   data-testid="social-linkedin">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="https://www.instagram.com/MONEYMANTEEARES" target="_blank" rel="noopener noreferrer"
                   className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-gold hover:text-slate-950 transition-all"
                   data-testid="social-instagram">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="https://www.facebook.com/profile.php?id=61577929918688" target="_blank" rel="noopener noreferrer"
                   className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-gold hover:text-slate-950 transition-all"
                   data-testid="social-facebook">
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Newsletter / CTA */}
            <div className="bg-slate-900/60 backdrop-blur-md border border-white/5 rounded-2xl p-8 md:p-12">
              <h3 className="font-heading text-2xl text-white font-semibold mb-4">
                Ready to Get Started?
              </h3>
              <p className="text-slate-400 mb-8">
                Apply online in minutes and get pre-approved fast. Or subscribe to receive tips and updates on the best loan programs.
              </p>

              <Button 
                onClick={handleApplyClick}
                className="w-full rounded-full bg-gold text-slate-950 font-bold text-lg py-6 hover:bg-gold-light hover:scale-[1.02] transition-all shadow-[0_0_30px_rgba(212,175,55,0.3)] mb-8"
                data-testid="contact-apply-btn"
              >
                Apply Now — Get Pre-Approved
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              <div className="border-t border-white/10 pt-8">
                <p className="text-white font-medium mb-4">Subscribe for Updates</p>
                <form onSubmit={handleSubscribe} className="flex gap-3">
                  <input 
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="flex-1 bg-slate-950/50 border border-slate-800 focus:border-gold focus:ring-1 focus:ring-gold rounded-lg px-4 py-3 text-white placeholder:text-slate-500 outline-none transition-all"
                    data-testid="newsletter-email"
                  />
                  <Button 
                    type="submit"
                    className="rounded-lg bg-slate-800 text-white hover:bg-slate-700 px-6 transition-all"
                    data-testid="newsletter-submit"
                  >
                    Subscribe
                  </Button>
                </form>
                {subscribeStatus && (
                  <p className="text-gold text-sm mt-3">{subscribeStatus}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-950 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://mmtainc.com/wp-content/uploads/2025/06/moneyman_logo_white-150x150.png" 
                alt="MoneyMan Tee Ares" 
                className="w-10 h-10"
              />
              <span className="font-heading text-xl text-white font-semibold">MoneyMan Tee Ares</span>
            </div>
            <div className="text-slate-500 text-sm text-center">
              © {new Date().getFullYear()} MoneyMan Tee Ares. All rights reserved. NMLS# Licensed | Equal Housing Opportunity
            </div>
            <div className="flex items-center gap-4">
              <a href="/login" className="text-slate-500 hover:text-gold text-sm transition-colors">Admin Login</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
